import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonComponent } from "@components/button/button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { NgbActiveModal, NgbModal, NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { CommandService } from "~app/common/services/cqs/command.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { TransporttilldelaArbetsorderCommand } from "~app/features/transport/transportplanera/models/commands";
import { ITransportforetagForTilldelningQueryResult } from "~app/features/transport/transportplanera/models/queries";
import { TplTransporttilldelningModified } from "../../../../events/tplEventHandler";
import { TplArbetsorderViewModel } from "../../../../models/tplListViewModels";
import { HandelssortimentDisplayMode } from "../../models/tpl-transporttilldelning.model";
import { TplTransporttilldelningAoComponent } from "../tpl-transporttilldelning-ao/tpl-transporttilldelning-ao.component";
import { TplTransporttilldelningHsComponent } from "../tpl-transporttilldelning-hs/tpl-transporttilldelning-hs.component";

@Component({
    selector: "rask-tpl-transporttilldelning-modal",
    templateUrl: "./tpl-transporttilldelning-modal.component.html",
    styleUrls: ["./tpl-transporttilldelning-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        BlockComponent,
        TplTransporttilldelningAoComponent,
        ActionButtonComponent,
        TplTransporttilldelningHsComponent,
        SaveButtonComponent,
        ButtonComponent,
        TranslatePipe
    ]
})
export class TplTransporttilldelningModalComponent implements OnInit {
    readonly activeModal = inject(NgbActiveModal);

    protected arbetsordrar: TplArbetsorderViewModel[];
    protected bransleValt: boolean;
    protected handelssortimentDisplayMode: HandelssortimentDisplayMode;
    protected selectedHs: number[];
    protected selectedTransportforetag: number;
    protected transportforetag: ITransportforetagForTilldelningQueryResult[];

    private readonly $translate = inject(TranslateService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly eventService = inject(EventService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly toastService = inject(ToastService);

    private form: UntypedFormGroup;

    static open(
        transportforetag: ITransportforetagForTilldelningQueryResult[],
        arbetsordrar: TplArbetsorderViewModel[],
        modalService: NgbModal,
        selectedHs: number[],
        bransleValt: boolean,
        callBack: (result: any) => void = null) {
        const options = {
            keyboard: false,
            backdrop: "static",
            centered: true,
            size: "lg"
        } as NgbModalOptions;
        const modalRef = modalService.open(this, options);
        const transportforetagIds = arbetsordrar.filter(x => x).map(x => x.model.transportforetagId);
        const tfAreEqual = transportforetagIds.every(t => t === transportforetagIds[0]);
        modalRef.componentInstance.init(
            transportforetag ? transportforetag : null,
            arbetsordrar,
            selectedHs ? selectedHs : [],
            bransleValt,
            tfAreEqual ? transportforetagIds[0] : null);
        if (callBack) {
            modalRef.result.then(r => callBack(r));
        } else {
            modalRef.result.then(r => modalRef.close(r));
        }
    }

    beforeDismiss() {
        return false;
    }

    canSave() {
        return this.form?.dirty || this.commandService.isDirty();
    }

    canToggleHandelssortiment(): boolean {
        return (this.arbetsordrar?.length === 1 || (this.selectedHs?.length === 1 && this.bransleValt)) &&
            this.handelssortimentDisplayMode !== HandelssortimentDisplayMode.all;
    }

    close(dismiss: boolean) {
        if (dismiss) {
            this.arbetsordrar.forEach(ao => {
                ao.showWarning = false;
            });
            this.activeModal.close(dismiss);
            return;
        }
        this.canDeactivate().then(res => {
            if (res) {
                this.arbetsordrar.forEach(ao => {
                    ao.showWarning = false;
                });
                this.activeModal.close(dismiss);
            }
        });
    }

    displayHandelssortiment(): boolean {
        return (this.arbetsordrar?.length === 1 || (this.selectedHs?.length === 1 && this.bransleValt)) &&
            this.handelssortimentDisplayMode !== HandelssortimentDisplayMode.none;
    }

    init(transportforetag: ITransportforetagForTilldelningQueryResult[],
        arbetsordrar: TplArbetsorderViewModel[],
        selectedHs: number[],
        bransleValt: boolean,
        selectedTransportforetag: number) {
        this.transportforetag = transportforetag ? transportforetag : null;
        this.arbetsordrar = arbetsordrar;
        this.selectedHs = selectedHs ? selectedHs : [];
        this.bransleValt = bransleValt;
        this.selectedTransportforetag = selectedTransportforetag;
    }

    ngOnInit(): void {
        this.initHandelssortimentDisplayMode();
    }

    onTilldelad(form: UntypedFormGroup) {
        this.form = form;
    }

    removeTilldelning() {
        const command = new TransporttilldelaArbetsorderCommand();
        command.arbetsorderIds = this.arbetsordrar.map(x => x.id);
        command.transportforetagId = null;

        this.raskModalService.confirm(new ModalSettings(this.$translate.instant("TPL_TRANSPORTTILLDELA_CONFIRM_TABORT_TITLE"),
            this.$translate.instant("TPL_TRANSPORTTILLDELA_CONFIRM_TABORT_MESSAGE")))
            .subscribe((result) => {
                if (result === ConfirmDialogResponse.save) {
                    this.commandService.addTransactionCommand(command);
                    this.triggerSave();
                }
            });
    }
    async save() {
        if (this.canSave()) {
            this.arbetsordrar.forEach(ao => {
                if (ao.showWarning) {
                    ao.warningEnabled = true;
                }
            });
            this.triggerSave();
        }
    }
    toggleHandelssortiment() {
        this.handelssortimentDisplayMode = HandelssortimentDisplayMode.all;
    }
    tryClose() {
        this.close(false);
    }
    private readonly canDeactivate = (): Promise<boolean> => new Promise(resolve => {
        if ((this.commandService.isDirty()) || this.canSave()) {
            const saveFn = !this.commandService.isDirty() && !this.canSave() ? (async () => {
                await this.save();
                resolve(true);
            }) : null;
            return this.dirtyFormService.dirtyFormModal(async () => {
                await this.commandService.reset();
                resolve(true);
            }, saveFn, (arg) => {
                resolve(false);
            });
        } else {
            resolve(true);
        }
        return undefined;
    });

    private initHandelssortimentDisplayMode(): void {
        const calculate = (): HandelssortimentDisplayMode => {
            if (this.selectedHs?.length === 1 && this.bransleValt) {
                return HandelssortimentDisplayMode.none;
            }
            const arbetsorder = this.arbetsordrar[0];
            if (!arbetsorder) {
                return HandelssortimentDisplayMode.none;
            }
            const handelssortimentLen = arbetsorder.model?.handelssortiment?.length ?? 0;
            if (!handelssortimentLen) {
                return HandelssortimentDisplayMode.none;
            }

            const deviating = arbetsorder.model.handelssortiment.filter(x => !!x.transportforetagId || !!x.befraktareId || !x.egenTransport);
            if (deviating.length === handelssortimentLen) {
                return HandelssortimentDisplayMode.all;
            }
            if (deviating.length) {
                return HandelssortimentDisplayMode.deviating;
            }

            return HandelssortimentDisplayMode.none;
        };

        this.handelssortimentDisplayMode = calculate();
    }

    private triggerSave() {
        this.busyService
            .addWork(this.commandService.pushCommandQueueTransaction())
            .subscribe(() => {
                this.eventService.emit(new TplTransporttilldelningModified());
                this.toastService.add("Ändringarna sparades");
                this.close(true);
            });
    }
}
